import { Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { api } from "../api/api";
import { getProfile } from "../api/userService";
import Layout from "../containers/Layout";
import { selectIsAuthenticated, setProfile } from "../redux/slices/authSlice";
import { RootState } from "../redux/store";
import { remapPaymentMethods } from "../types/paymentTypes";

const PrivateRoutes = () => {
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const profile = useSelector((state: RootState) => state.auth.profile);
  const location = useLocation();
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || profile || isLoadingProfile) {
      return;
    }

    let isActive = true;

    setIsLoadingProfile(true);

    api(getProfile())
      .then((profileData) => {
        if (!isActive) {
          return;
        }

        remapPaymentMethods(profileData);
        dispatch(setProfile({ profile: profileData.data }));
      })
      .catch((error) => {
        if (!isActive) {
          return;
        }

        showBoundary(error);
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingProfile(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [dispatch, isAuthenticated, isLoadingProfile, profile, showBoundary]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!profile) {
    return (
      <Layout isAuthenticated={true}>
        <Box
          sx={{
            minHeight: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout isAuthenticated={true}>
      <Outlet />
    </Layout>
  );
};

export default PrivateRoutes;
